// Unpublished Work © 2026 Deere & Company.

import {MINUTES_PER_HOUR} from 'Ui/constants/time-constants';
import {formatNumber, getFormattedCurrency} from 'Utils/unit-conversion-utils';
import {ACTUAL_COST} from 'OnLabor/report/constants/labor-report';
import moment from 'moment';
import {DATE_FORMATS} from 'Src/ui-core/utils/time-utils';
import {sortBy, uniqBy, groupBy, sumBy} from 'lodash';

const TOTAL_FIELD = 'total';
const UNASSIGNED_AREA_ID = 'unassigned';
const CHILD_ROWS_KEY = {
    jobCategories: 'jobTemplates',
    areas: 'areaJobTemplates'
};
const TWO = 2;
const FIVE = 5;
const SIX = 6;
const FOUR = 4;
const ELEVEN = 11;
const TOTAL_ROW_ID = 'totals';

export const CUSTOM_ROW_STYLE = {
    height: '100%',
    '& .MuiDataGrid-treeDataGroupingCell': {
        height: '100%'
    },
    [`&& .MuiDataGrid-row[data-id="${TOTAL_ROW_ID}"]`]: {
        bgcolor: '#C7C6C5',
        fontWeight: 'fontWeightMedium',
        '& .MuiDataGrid-cell--pinnedLeft': {
            bgcolor: '#C7C6C5'
        },
        '&:hover, &:hover .MuiDataGrid-cell--pinnedLeft': {
            bgcolor: '#C7C6C5'
        }
    }
};

function getJobTemplateArea(templateAreaMap, jobTemplateId, translations) {
    const area = templateAreaMap.get(jobTemplateId);

    return area?.id ? area : {
        id: UNASSIGNED_AREA_ID,
        name: translations.ONLINK_UNASSIGNED
    };
}

export function getJobTemplateAreaMap(templates = []) {
    return new Map(templates.map(({
        equipmentArea, equipmentAreaId, jobTemplateId
    }) => [
        jobTemplateId,
        {
            id: equipmentAreaId,
            name: equipmentArea
        }
    ]));
}

export function mapAreaData(laborReportData, templateAreaMap, translations) {
    const jobTemplates = (laborReportData.jobTemplates ?? []).filter(({locationId}) => locationId);
    const areaJobTemplates = jobTemplates.map((row) => ({
        ...row,
        locationParentAltId: getJobTemplateArea(templateAreaMap, row.locationId, translations).id
    }));

    const areas = jobTemplates.map((row) => {
        const area = getJobTemplateArea(templateAreaMap, row.locationId, translations);

        return {
            ...row,
            locationId: area.id,
            name: area.name
        };
    });

    return {
        ...laborReportData,
        areas,
        areaJobTemplates
    };
}

export function getDateRangePresets(translations) {
    return [
        {
            id: 'last3months',
            title: translations.LAST_3_MONTHS,
            getSelectedDates: () => [
                moment().subtract(TWO, 'months').startOf('month').toDate(),
                moment().endOf('day').toDate()
            ]
        },
        {
            id: 'last6months',
            title: translations.LAST_6_MONTHS,
            getSelectedDates: () => [
                moment().subtract(FIVE, 'months').startOf('month').toDate(),
                moment().endOf('day').toDate()
            ]
        },
        {
            id: 'thisYear',
            title: translations.THIS_YEAR,
            getSelectedDates: () => [moment().startOf('year').toDate(), moment().endOf('day').toDate()]
        },
        {
            id: 'rolling12months',
            title: translations.ROLLING_12_MONTHS,
            getSelectedDates: () => [
                moment().subtract(ELEVEN, 'months').startOf('month').toDate(),
                moment().endOf('day').toDate()
            ]
        },
        {
            id: 'lastYear',
            title: translations.LAST_YEAR,
            getSelectedDates: () => [moment().startOf('year').subtract(1, 'year').toDate(), moment().endOf('year').subtract(1, 'year').toDate()]
        }
    ];
}

export function getFilterItems(laborReportData, tab) {
    const rows = laborReportData[tab] ?? [];

    const items = uniqBy(rows.filter(({locationId}) => locationId), 'locationId')
        .map(({
            locationId, name
        }) => ({
            id: locationId,
            title: name
        }));

    return sortBy(items, ({title}) => title?.toLowerCase());
}

export function getMonthKeys(startDate, endDate) {
    const months = [];
    const cursor = moment(startDate, DATE_FORMATS.day).startOf('month');
    const end = moment(endDate, DATE_FORMATS.day).endOf('month');

    while (cursor.isSameOrBefore(end)) {
        months.push(cursor.format(DATE_FORMATS.month));
        cursor.add(1, 'month');
    }
    return months;
}

function sumByMonth(rows, metric, monthKeys) {
    const byMonth = groupBy(rows, ({dateSelect}) => dateSelect.slice(0, SIX));
    const values = monthKeys.reduce((acc, month) => {
        acc[month] = sumBy(byMonth[month] ?? [], (row) => row[metric] ?? 0);

        return acc;
    }, {});

    values[TOTAL_FIELD] = monthKeys.reduce((sum, month) => sum + values[month], 0);

    return values;
}

function buildRow(rows, path, metric, monthKeys) {
    const [{
        locationId, name
    }] = rows;

    return {
        id: path.join('/'),
        locationId,
        name,
        path,
        ...sumByMonth(rows, metric, monthKeys)
    };
}

export function getTreeRows({
    laborReportData, metric, monthKeys, selectedIds, tab
}) {
    const parentRowsById = groupBy(laborReportData[tab] ?? [], 'locationId');
    const childRowsByParent = groupBy(laborReportData[CHILD_ROWS_KEY[tab]] ?? [], 'locationParentAltId');

    const parentIds = Object.keys(parentRowsById)
        .filter((id) => id !== 'undefined' && id !== 'null')
        .filter((id) => selectedIds.length === 0 || selectedIds.includes(id));

    return parentIds.flatMap((parentId) => {
        const parentRow = buildRow(parentRowsById[parentId], [parentId], metric, monthKeys);

        const childRows = Object.values(groupBy(childRowsByParent[parentId] ?? [], 'locationId'))
            .map((templateRows) => buildRow(templateRows, [parentId, templateRows[0].locationId], metric, monthKeys));

        return [parentRow, ...sortBy(childRows, ({name}) => name?.toLowerCase())];
    });
}

function formatMetricValue(value, metric, currencyPreference) {
    if (metric === ACTUAL_COST) {
        return getFormattedCurrency(value, {
            currencyPreference
        });
    }

    return formatNumber(value / MINUTES_PER_HOUR, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
}

export function getDataGridColumns({
    monthKeys, metric, membership, translations
}) {
    const spansYears = monthKeys[0]?.slice(0, FOUR) !== monthKeys.at(-1)?.slice(0, FOUR);
    const createValueColumn = (field, headerName) => ({
        field,
        headerName,
        minWidth: 120,
        flex: 1,
        type: 'number',
        valueFormatter: (value) => formatMetricValue(value, metric, membership.currencyPreference)
    });

    return [
        ...monthKeys.map((month) => createValueColumn(
            month,
            moment(month, DATE_FORMATS.month).format(spansYears ? 'MMM YYYY' : 'MMM')
        )),
        createValueColumn(TOTAL_FIELD, translations.ONLINK_TOTAL)
    ];
}

export function getTotalsRow(treeRows, monthKeys, translations) {
    const parentRows = treeRows.filter(({path}) => path.length === 1);

    return [...monthKeys, TOTAL_FIELD].reduce((acc, field) => {
        acc[field] = sumBy(parentRows, field);

        return acc;
    }, {
        id: TOTAL_ROW_ID,
        name: translations.TOTALS,
        path: [TOTAL_ROW_ID]
    });
}
